// Central simulation state and the fixed update order (plan §4.2 / §4.4).
// Plain data + pools only - NO Pixi objects here. The renderer reads this; it never
// writes gameplay truth back.

import { clamp } from '../core/math';
import { Rng } from '../core/rng';
import { SpatialHash } from '../core/spatialHash';
import type { Input } from '../core/input';
import {
  ARENA,
  ENEMY_SHOT,
  HASH_CELL,
  MAGNET,
  PLAYER,
  VIEW,
  WET,
  ALTAR,
  DIFFICULTY_MODES,
  xpToNext,
} from './balance';
import type { DifficultyMode } from './balance';
import {
  abilityDefinitionsForLevel,
  createAbilitySystem,
  usesHiddenBaseAbilities,
} from './activeAbilities';
import type { ActiveAbilitySystem } from './activeAbilities';
import type { AbilityDef } from './balance';
import { createEnemyPool, separateEnemies, spawnEnemy, updateEnemies } from './enemies';
import type { Enemy } from './enemies';
import { createEnemyShotPool, spawnEnemyShot, updateEnemyShots } from './enemyShots';
import { buildBoulderHash, generateBoulders, pushOutOfBoulders } from './boulders';
import type { Boulder } from './boulders';
import { buildPuddleHash, generatePuddles, inPuddle } from './puddles';
import type { Puddle } from './puddles';
import {
  createBeamPool,
  createDamageNumberPool,
  createParticlePool,
  createRingPool,
  spawnDeathBurst,
  spawnImplosion,
  spawnNova,
  spawnRing,
  updateBeams,
  updateDamageNumbers,
  updateParticles,
  updateRings,
} from './fx';

/** How long an event/boss banner stays on screen. */
const BANNER_SEC = 2.6;
/** Duration of the red screen flash when the player takes a hit. */
const HURT_FLASH_SEC = 0.3;
/** Spor Sayer (hidden variant) fires from closer than the mortar's ENEMY_SHOT.rangePx. */
const SPORE_FIRE_RANGE = 250;
import { WeaponSystem, createProjectilePool } from './weapons';
import {
  createHealthOrbPool,
  createMotePool,
  createNectarPool,
  spawnHealthOrb,
  spawnMote,
  spawnNectar,
} from './pickups';
import { Spawner } from './spawner';
import type { LevelDefinition } from './levels';
import { applyContactDamage, updateProjectiles } from './combat';
import type { HitCtx } from './combat';
import { computeModifiers, neutralMods } from './passives';
import type { Modifiers } from './passives';
import { rollDraft } from './upgrades';
import type { DraftChoice } from './upgrades';
import { computeScore } from './score';

export interface PlayerState {
  x: number;
  y: number;
  prevX: number;
  prevY: number;
  hp: number;
  maxHp: number;
  radius: number;
  iFrameSec: number;
  wetSec: number; // slowed by a water puddle (lingers after leaving)
}

/** Semantic gameplay feedback consumed by the native iOS shell when available. */
export type GameHapticEvent = 'ability' | 'levelUp' | 'boss' | 'damageHeavy' | 'victory' | 'defeat' | 'selection';

/** A telegraphed Bloom Vine wall. It becomes solid only after its warning period, so a
 * player is never boxed in without a visible escape window. */
export interface VineWall extends Boulder {
  telegraphSec: number;
  lifeSec: number;
}

/** A collected nectar turns into this damage-amplifying pool when the player owns the
 * Nectar Catalyst reaction. It is deliberately capped and short-lived for readability. */
export interface CatalystPool {
  x: number;
  y: number;
  radius: number;
  lifeSec: number;
}

/** A narrow directional lane. Its warning period is public so the renderer can keep
 * gameplay and telegraph in lockstep. */
export interface GravityRail {
  x: number;
  y: number;
  dx: number;
  dy: number;
  length: number;
  width: number;
  telegraphSec: number;
  lifeSec: number;
}

export class World {
  readonly levelDefinition: LevelDefinition;
  readonly player: PlayerState;
  readonly enemies = createEnemyPool();
  readonly projectiles = createProjectilePool();
  readonly motes = createMotePool();
  readonly nectars = createNectarPool();
  readonly healthOrbs = createHealthOrbPool();
  readonly enemyShots = createEnemyShotPool();
  readonly damageNumbers = createDamageNumberPool();
  readonly particles = createParticlePool();
  readonly beams = createBeamPool();
  readonly rings = createRingPool();
  readonly weapons = new WeaponSystem();
  readonly abilities: ActiveAbilitySystem;
  readonly abilityDefs: ReadonlyArray<AbilityDef>;
  readonly usesHiddenAbilitySystem: boolean;

  readonly boulders: Boulder[];
  private readonly boulderHash: SpatialHash;
  private readonly boulderBuf: number[] = [];

  /** Temporary boulder ring (hidden variant, Maindan). Empty on the base game, so the extra
   *  collision passes below are no-ops there. Public so the renderer can draw it. */
  tempBoulders: Boulder[] = [];
  private tempBoulderHash: SpatialHash | null = null;

  readonly puddles: Puddle[];
  private readonly puddleHash: SpatialHash;
  private readonly puddleBuf: number[] = [];

  /** Verdant Still's temporary terrain. `activeVineWalls` is the collision subset; the
   * renderer receives the full list to show its telegraph. */
  readonly vineWalls: VineWall[] = [];
  private readonly activeVineWalls: VineWall[] = [];
  private readonly vineHash = new SpatialHash(HASH_CELL);
  private readonly vineBuf: number[] = [];
  readonly catalystPools: CatalystPool[] = [];
  /** Starfall Conduit's telegraphed, non-damaging directional terrain. */
  readonly gravityRails: GravityRail[] = [];

  readonly hash = new SpatialHash(HASH_CELL); // public so the ability system can query it
  private readonly rng: Rng;
  private readonly spawner: Spawner;
  private readonly passiveLevels = new Map<string, number>();
  private readonly allowedWeaponKeys: ReadonlySet<string>;
  private readonly allowedPassiveKeys: ReadonlySet<string>;
  private mods: Modifiers = neutralMods();
  readonly hitCtx: HitCtx; // public so the ability system can deal damage
  private readonly queryBuf: number[] = [];
  private readonly splashBuf: number[] = [];

  elapsedSec = 0;
  kills = 0;
  bossKills = 0;
  level = 1;
  xp = 0;
  xpNeeded = xpToNext(1);
  pendingLevelUps = 0;
  gameOver = false;
  victory = false;
  private catalystSeen = false; // a Catalyst (final boss) has appeared - kill them all to win
  /** Difficulty multipliers (set from the start screen before the run begins). */
  difficulty: DifficultyMode = DIFFICULTY_MODES.normal;
  /** Secret kid mode: every active ability is castable from the start (ignores unlock levels).
   *  Set from the start screen; runs with it on are never ranked. */
  allAbilities = false;
  viewHalfW: number = VIEW.defaultHalfW;
  viewHalfH: number = VIEW.defaultHalfH;
  /** Every living scheduled boss (for the stacked health bars). With the NinjaKid Dojo's
   * two-minute cadence several can be alive at once - none may vanish from the HUD. */
  readonly activeBosses: Enemy[] = [];
  /** Transient on-screen banner ("THE SURGE", boss names...). */
  bannerText = '';
  bannerSec = 0;
  /** Screen-shake trauma [0,1]; the renderer turns it into a camera offset. */
  shakeTrauma = 0;
  /** Red hurt-flash timer; the HUD turns it into a full-screen red tint. */
  hurtFlashSec = 0;
  /** Healing-altar cooldown remaining (0 = ready); the renderer shows the state. */
  altarCd = 0;
  private readonly hapticEvents: GameHapticEvent[] = [];
  movementX = 1;
  movementY = 0;

  constructor(seed: number, levelDefinition: LevelDefinition) {
    this.levelDefinition = levelDefinition;
    this.rng = new Rng(seed);
    this.spawner = new Spawner(this.rng, levelDefinition);
    this.allowedWeaponKeys = new Set(levelDefinition.weaponKeys);
    this.allowedPassiveKeys = new Set(levelDefinition.passiveKeys);
    this.usesHiddenAbilitySystem = usesHiddenBaseAbilities(levelDefinition);
    this.abilityDefs = abilityDefinitionsForLevel(levelDefinition);
    this.abilities = createAbilitySystem(this.abilityDefs, this.usesHiddenAbilitySystem);
    this.boulders = generateBoulders(seed);
    this.boulderHash = buildBoulderHash(this.boulders, HASH_CELL);
    this.puddles = generatePuddles(seed);
    this.puddleHash = buildPuddleHash(this.puddles, HASH_CELL);
    const cx = ARENA.width / 2;
    const cy = ARENA.height / 2;
    this.player = {
      x: cx,
      y: cy,
      prevX: cx,
      prevY: cy,
      hp: PLAYER.maxHp,
      maxHp: PLAYER.maxHp,
      radius: PLAYER.radius,
      iFrameSec: 0,
      wetSec: 0,
    };
    this.weapons.add(levelDefinition.startingWeaponKey);
    this.hitCtx = {
      rng: this.rng,
      mods: this.mods,
      enemies: this.enemies,
      hash: this.hash,
      numbers: this.damageNumbers,
      beams: this.beams,
      boulders: this.boulders,
      boulderHash: this.boulderHash,
      boulderBuf: this.boulderBuf,
      vineWalls: this.activeVineWalls,
      vineHash: this.vineHash,
      vineBuf: this.vineBuf,
      queryBuf: this.queryBuf,
      splashBuf: this.splashBuf,
      playerX: this.player.x,
      playerY: this.player.y,
      dmgMul: 1,
      dmgMulClose: 1,
      closeRadius2: 0,
      damageFieldMul: (x, y) => this.catalystDamageMultiplier(x, y) * this.abilities.damageFieldMul,
      damageDealt: 0,
    };
  }

  update(dt: number, input: Input): void {
    if (this.gameOver) return;
    this.elapsedSec += dt;
    this.updateVineWalls(dt);
    this.updateCatalystPools(dt);
    this.updateGravityRails(dt);
    const p = this.player;
    const mods = this.mods;

    // 1. Player movement.
    p.prevX = p.x;
    p.prevY = p.y;
    input.sample();
    const movementLength = Math.hypot(input.move.x, input.move.y);
    if (movementLength > 0.12) {
      this.movementX = input.move.x / movementLength;
      this.movementY = input.move.y / movementLength;
    }

    // Abilities: cast queued hotkey presses, tick cooldowns/timers, refresh buffs.
    const presses = input.abilityPresses;
    for (let i = 0; i < presses.length; i++) this.abilities.activateByHotkey(presses[i], this.level, this);
    input.clearAbilityPresses();
    this.abilities.update(dt, this);

    let speed = PLAYER.speedPerSec * mods.moveSpeedMul * this.abilities.speedMul;
    if (p.wetSec > 0) {
      speed *= WET.slowMul + (1 - WET.slowMul) * mods.wetResist;
      p.wetSec -= dt;
    }
    p.x += input.move.x * speed * dt;
    p.y += input.move.y * speed * dt;
    p.x = clamp(p.x, p.radius, ARENA.width - p.radius);
    p.y = clamp(p.y, p.radius, ARENA.height - p.radius);
    pushOutOfBoulders(p, p.radius, this.boulders, this.boulderHash, this.boulderBuf);
    if (this.tempBoulderHash) pushOutOfBoulders(p, p.radius, this.tempBoulders, this.tempBoulderHash, this.boulderBuf);
    pushOutOfBoulders(p, p.radius, this.activeVineWalls, this.vineHash, this.vineBuf);
    this.applyGravityToPlayer(dt);
    if (inPuddle(p.x, p.y, this.puddles, this.puddleHash, this.puddleBuf)) p.wetSec = WET.durationSec;
    // Healing altar: stand on it (when ready) to heal a chunk of max HP, then it cools down.
    if (this.altarCd > 0) this.altarCd -= dt;
    const adx = p.x - ALTAR.x;
    const ady = p.y - ALTAR.y;
    const ar = ALTAR.radius + p.radius;
    if (this.altarCd <= 0 && adx * adx + ady * ady <= ar * ar) {
      p.hp = Math.min(p.maxHp, p.hp + p.maxHp * ALTAR.healFrac);
      this.altarCd = ALTAR.cooldownSec;
      this.fxBurst(p.x, p.y, 0x59ffd0, 16);
    }
    // Feed current player pos + ability damage buffs into the hit context (weapons read these).
    this.hitCtx.playerX = p.x;
    this.hitCtx.playerY = p.y;
    this.hitCtx.dmgMul = this.abilities.dmgMul;
    this.hitCtx.dmgMulClose = this.abilities.dmgMulClose;
    this.hitCtx.closeRadius2 = this.abilities.closeRadius2;
    if (mods.regenPerSec > 0 && p.hp < p.maxHp) {
      p.hp = Math.min(p.maxHp, p.hp + mods.regenPerSec * dt);
    }

    // 2. Spawn enemies + run the timed schedule (bosses / events).
    const spawn = this.spawner.update(
      dt,
      this.elapsedSec,
      p.x,
      p.y,
      this.enemies,
      this.difficulty.spawnMul,
      this.difficulty.enemyHpMul,
    );
    if (spawn.banner !== null) {
      this.bannerText = spawn.banner;
      this.bannerSec = BANNER_SEC;
    }
    if (spawn.boss !== null) {
      this.activeBosses.push(spawn.boss);
      this.emitHaptic('boss');
    }
    if (spawn.bloomWallCount > 0) this.triggerBloomPulse(spawn.bloomWallCount);
    if (spawn.gravityRailCount > 0) this.triggerGravityRails(spawn.gravityRailCount);

    // 3. Enemies chase (+ status ticks).
    updateEnemies(this.enemies, dt, p.x, p.y);

    // 3b. Ranged fire (mortar) + boss summon.
    this.updateBehaviors(dt);
    this.applyGravityToEnemies(dt);
    this.applyMagnetBeetles(dt);

    // 4. Build hash, push overlapping enemies apart, rebuild (positions changed).
    this.rebuildHash();
    separateEnemies(this.enemies, this.hash, this.queryBuf);
    this.resolveEnemyTerrain();
    this.rebuildHash();

    // 5. Weapons fire (targets only on-screen enemies; deals crit/splash damage).
    this.weapons.update(dt, p.x, p.y, this.projectiles, this.hitCtx, this.viewHalfW, this.viewHalfH);

    // 6. Projectiles move + hit enemies.
    this.applyGravityToProjectiles(dt);
    updateProjectiles(this.projectiles, dt, this.hitCtx);

    // 6b. Enemy projectiles (mortar shots) vs the player.
    const inv = this.abilities.invuln;
    const dmgTakenMul = this.difficulty.dmgTakenMul;
    const resolvedDamageTaken = dmgTakenMul * this.abilities.damageTakenMul;
    const shotHit = updateEnemyShots(
      this.enemyShots, dt, p, this.boulders, this.boulderHash, this.boulderBuf,
      this.activeVineWalls, this.vineHash, this.vineBuf, inv, resolvedDamageTaken,
    );

    // 7. Enemy contact damage (before reaping - hash indices still valid).
    const contactHit = applyContactDamage(
      this.enemies,
      this.hash,
      p,
      this.queryBuf,
      dt,
      inv,
      resolvedDamageTaken,
      (enemy) => this.abilities.onContactHit(enemy, this),
    );
    if (shotHit || contactHit) {
      this.shakeTrauma = Math.min(1, this.shakeTrauma + 0.35);
      this.hurtFlashSec = HURT_FLASH_SEC;
      this.emitHaptic('damageHeavy');
    }

    // 8. Reap dead enemies → XP motes (+ split for vats, + reward for bosses).
    for (let i = 0; i < this.enemies.count; i++) {
      const e = this.enemies.items[i];
      if (e.hp > 0) continue;
      this.kills++;
      const sx = e.x;
      const sy = e.y;
      const type = this.entityType(e);
      this.abilities.onEnemyKilled(e, this);
      spawnDeathBurst(this.particles, sx, sy, e.color, this.rng, e.isBoss ? 18 : 4);
      if (e.isBoss) {
        // Boss reward: a burst of XP (a proper chest arrives with the hazards batch).
        const each = Math.max(1, Math.ceil(e.xp / 6));
        for (let k = 0; k < 6; k++) {
          spawnMote(this.motes, sx + this.rng.range(-24, 24), sy + this.rng.range(-24, 24), each);
        }
        const bossIndex = this.activeBosses.indexOf(e);
        if (bossIndex >= 0) this.activeBosses.splice(bossIndex, 1);
        this.bossKills++;
        this.shakeTrauma = Math.min(1, this.shakeTrauma + 0.7);
      } else {
        spawnMote(this.motes, sx, sy, e.xp);
      }
      if (type.onDeath?.dropsNectar) spawnNectar(this.nectars, sx, sy);
      if (
        this.difficulty.healthOrbHealFrac > 0 &&
        (e.isBoss || this.rng.next() < this.difficulty.healthOrbDropChance)
      ) {
        spawnHealthOrb(
          this.healthOrbs,
          sx,
          sy,
          this.difficulty.healthOrbHealFrac,
        );
      }
      if (type.onDeath?.bloomWallCount !== undefined) this.triggerBloomPulse(type.onDeath.bloomWallCount, sx, sy);
      this.enemies.releaseAt(i);
      i--;
      const split = type.split;
      for (let k = 0; split !== undefined && k < split.count; k++) {
        spawnEnemy(
          this.enemies,
          this.enemyType(split.enemyKey),
          sx + this.rng.range(-14, 14),
          sy + this.rng.range(-14, 14),
          1,
          1,
          this.rng.next(),
        );
      }
    }

    // Verdant nectar rewards position-aware Rootling kills. It does not use the XP
    // magnet: a player chooses whether to step into the recovery moment.
    for (let i = 0; i < this.nectars.count; i++) {
      const nectar = this.nectars.items[i];
      nectar.lifeSec -= dt;
      if (nectar.lifeSec <= 0) {
        this.nectars.releaseAt(i--);
        continue;
      }
      const dx = p.x - nectar.x;
      const dy = p.y - nectar.y;
      const rr = p.radius + PLAYER.pickupRadius;
      if (dx * dx + dy * dy <= rr * rr) {
        if (this.weapons.has('nectarCatalyst')) {
          this.spawnCatalystPool(nectar.x, nectar.y);
          this.fxRing(nectar.x, nectar.y, 46, 0xffc35a, 0.45);
        } else {
          p.hp = Math.min(p.maxHp, p.hp + p.maxHp * 0.14);
          this.fxBurst(nectar.x, nectar.y, 0xffc35a, 10);
        }
        this.nectars.releaseAt(i--);
      }
    }

    // Recovery orbs remain available at full health, so touching one never wastes it.
    for (let i = 0; i < this.healthOrbs.count; i++) {
      const orb = this.healthOrbs.items[i];
      orb.lifeSec -= dt;
      if (orb.lifeSec <= 0) {
        this.healthOrbs.releaseAt(i--);
        continue;
      }
      const dx = p.x - orb.x;
      const dy = p.y - orb.y;
      const rr = p.radius + PLAYER.pickupRadius;
      if (p.hp < p.maxHp && dx * dx + dy * dy <= rr * rr) {
        p.hp = Math.min(p.maxHp, p.hp + p.maxHp * orb.healFrac);
        this.fxRing(orb.x, orb.y, 52, 0x59ffd0, 0.5);
        this.fxBurst(orb.x, orb.y, 0x59ffd0, 12);
        this.healthOrbs.releaseAt(i--);
      }
    }

    // 9. Magnet + collect XP motes.
    const magnet = MAGNET.baseRange + mods.magnetAdd;
    const mag2 = magnet * magnet;
    const collect = p.radius + PLAYER.pickupRadius;
    const collect2 = collect * collect;
    for (let i = 0; i < this.motes.count; i++) {
      const m = this.motes.items[i];
      this.applyGravityToPosition(m, 2, dt);
      const dx = p.x - m.x;
      const dy = p.y - m.y;
      const d2 = dx * dx + dy * dy;
      if (d2 <= collect2) {
        this.addXp(m.value);
        this.motes.releaseAt(i);
        i--;
        continue;
      }
      if (d2 <= mag2) {
        const d = Math.sqrt(d2) || 1;
        const step = (MAGNET.pullSpeedPerSec * dt) / d;
        m.x += dx * step;
        m.y += dy * step;
      }
    }

    // 9b. Visual FX + banner timer + victory at the run-length mark.
    updateDamageNumbers(this.damageNumbers, dt);
    updateParticles(this.particles, dt);
    updateBeams(this.beams, dt);
    updateRings(this.rings, dt);
    if (this.shakeTrauma > 0) this.shakeTrauma = Math.max(0, this.shakeTrauma - dt * 1.8);
    if (this.hurtFlashSec > 0) this.hurtFlashSec = Math.max(0, this.hurtFlashSec - dt);
    if (this.bannerSec > 0) this.bannerSec -= dt;
    // The level's final boss appears at its run-length mark. Defeating it is the win;
    // merely surviving the timer is not enough.
    if (!this.victory && this.elapsedSec >= this.levelDefinition.runLengthSec) {
      let catalystAlive = 0;
      for (let i = 0; i < this.enemies.count; i++) {
        const e = this.enemies.items[i];
        if (e.isBoss && e.sprite === this.levelDefinition.victoryBossKey) catalystAlive++;
      }
      if (catalystAlive > 0) this.catalystSeen = true;
      else if (this.catalystSeen) this.victory = true;
    }

    // 10. End the run - on death (loss), or once the Catalyst is defeated (win).
    if (p.hp <= 0) {
      p.hp = 0;
      this.gameOver = true;
      this.emitHaptic('defeat');
    } else if (this.victory) {
      this.gameOver = true;
      this.emitHaptic('victory');
    }
  }

  // Creature behavior is read from its level data. Capturing the count up front means
  // newly-summoned enemies are not processed in their spawning tick.
  private updateBehaviors(dt: number): void {
    const p = this.player;
    const n = this.enemies.count;
    for (let i = 0; i < n; i++) {
      const e = this.enemies.items[i];
      const type = this.entityType(e);
      const bloom = type.bloomEmitter;
      if (bloom !== undefined) {
        e.bloomCd -= dt;
        if (e.bloomCd <= 0) {
          this.triggerBloomPulse(bloom.wallCount, e.x, e.y);
          e.bloomCd = bloom.cooldownSec;
        }
      }
      const rails = type.railEmitter;
      if (rails !== undefined) {
        e.railCd -= dt;
        if (e.railCd <= 0) {
          if (rails.rerouteActive) this.rerouteGravityRails(e.x, e.y);
          this.triggerGravityRails(rails.railCount, e.x, e.y);
          e.railCd = rails.cooldownSec;
        }
      }
      if (e.kind === 'ranged') {
        e.fireCd -= dt;
        if (e.fireCd <= 0) {
          const dx = p.x - e.x;
          const dy = p.y - e.y;
          const attack = type.rangedAttack;
          const fireRange = attack?.rangePx ?? (e.sprite === 'spore' ? SPORE_FIRE_RANGE : ENEMY_SHOT.rangePx);
          if (dx * dx + dy * dy <= fireRange * fireRange) {
            const sprite = attack?.sprite ?? (e.sprite === 'spore' ? 'note' : 'shot');
            const color = attack?.color ?? e.color;
            spawnEnemyShot(this.enemyShots, e.x, e.y, p.x, p.y, sprite, color, attack);
            e.fireCd = ENEMY_SHOT.cooldownSec;
          }
        }
      } else if (e.kind === 'summon') {
        e.summonCd -= dt;
        if (e.summonCd <= 0) {
          const summon = type.summon;
          if (summon === undefined) continue;
          for (let k = 0; k < summon.count; k++) {
            spawnEnemy(
              this.enemies,
              this.enemyType(summon.enemyKey),
              e.x + this.rng.range(-30, 30),
              e.y + this.rng.range(-30, 30),
              1,
              1,
              this.rng.next(),
            );
          }
          e.summonCd = summon.cooldownSec;
        }
      }
    }
  }

  /** Set the targeting viewport half-extents (world px) so weapons skip off-screen foes. */
  setView(halfW: number, halfH: number): void {
    this.viewHalfW = halfW;
    this.viewHalfH = halfH;
  }

  randomRange(min: number, max: number): number {
    return this.rng.range(min, max);
  }

  /** Move along the last meaningful input direction and resolve the same terrain
   * collisions as ordinary movement. Used by Starfall's Rail Surge. */
  dashPlayer(distance: number): { x1: number; y1: number; x2: number; y2: number } {
    const player = this.player;
    const x1 = player.x;
    const y1 = player.y;
    player.x = clamp(player.x + this.movementX * distance, player.radius, ARENA.width - player.radius);
    player.y = clamp(player.y + this.movementY * distance, player.radius, ARENA.height - player.radius);
    pushOutOfBoulders(player, player.radius, this.boulders, this.boulderHash, this.boulderBuf);
    if (this.tempBoulderHash) {
      pushOutOfBoulders(player, player.radius, this.tempBoulders, this.tempBoulderHash, this.boulderBuf);
    }
    pushOutOfBoulders(player, player.radius, this.activeVineWalls, this.vineHash, this.vineBuf);
    return { x1, y1, x2: player.x, y2: player.y };
  }

  /** Queue semantic feedback without coupling deterministic simulation to a platform API. */
  emitHaptic(event: GameHapticEvent): void {
    this.hapticEvents.push(event);
  }

  /** Deliver queued feedback and clear it without creating per-frame garbage. */
  flushHapticEvents(listener?: (event: GameHapticEvent) => void): void {
    if (listener !== undefined) {
      for (let i = 0; i < this.hapticEvents.length; i++) listener(this.hapticEvents[i]);
    }
    this.hapticEvents.length = 0;
  }

  private rebuildHash(): void {
    this.hash.clear();
    for (let i = 0; i < this.enemies.count; i++) {
      const e = this.enemies.items[i];
      this.hash.insert(i, e.x, e.y);
    }
  }

  // Post-movement terrain resolution per enemy: keep inside the arena, push out of
  // boulders, get wet in puddles. The arena clamp matters most for ranged standoff
  // enemies - without it a cornered kiter backs out through the wall where the (clamped)
  // player and the on-screen-only weapon targeting can never reach it again.
  private resolveEnemyTerrain(): void {
    for (let i = 0; i < this.enemies.count; i++) {
      const e = this.enemies.items[i];
      e.x = clamp(e.x, e.radius, ARENA.width - e.radius);
      e.y = clamp(e.y, e.radius, ARENA.height - e.radius);
      pushOutOfBoulders(e, e.radius, this.boulders, this.boulderHash, this.boulderBuf);
      if (this.tempBoulderHash) pushOutOfBoulders(e, e.radius, this.tempBoulders, this.tempBoulderHash, this.boulderBuf);
      pushOutOfBoulders(e, e.radius, this.activeVineWalls, this.vineHash, this.vineBuf);
      if (inPuddle(e.x, e.y, this.puddles, this.puddleHash, this.puddleBuf)) e.wetSec = WET.durationSec;
    }
  }

  private applyGravityToPlayer(dt: number): void {
    const p = this.player;
    if (!this.applyGravityToPosition(p, p.radius, dt)) return;
    p.x = clamp(p.x, p.radius, ARENA.width - p.radius);
    p.y = clamp(p.y, p.radius, ARENA.height - p.radius);
    pushOutOfBoulders(p, p.radius, this.boulders, this.boulderHash, this.boulderBuf);
    if (this.tempBoulderHash) pushOutOfBoulders(p, p.radius, this.tempBoulders, this.tempBoulderHash, this.boulderBuf);
    pushOutOfBoulders(p, p.radius, this.activeVineWalls, this.vineHash, this.vineBuf);
  }

  private applyGravityToEnemies(dt: number): void {
    for (let i = 0; i < this.enemies.count; i++) {
      const e = this.enemies.items[i];
      this.applyGravityToPosition(e, e.radius, dt);
    }
  }

  private applyGravityToProjectiles(dt: number): void {
    for (let i = 0; i < this.projectiles.count; i++) {
      const projectile = this.projectiles.items[i];
      if (projectile.railChargedSec > 0) projectile.railChargedSec = Math.max(0, projectile.railChargedSec - dt);
      if (this.applyGravityToPosition(projectile, projectile.radius, dt)) projectile.railChargedSec = 0.28;
    }
    for (let i = 0; i < this.enemyShots.count; i++) {
      const shot = this.enemyShots.items[i];
      this.applyGravityToPosition(shot, shot.radius, dt);
    }
  }

  /** Magnet Beetles pull local bodies into a readable cluster. It deliberately never
   * moves the player; its threat comes from arranging the rest of the fight. */
  private applyMagnetBeetles(dt: number): void {
    for (let i = 0; i < this.enemies.count; i++) {
      const magnet = this.enemies.items[i];
      if (magnet.hp <= 0) continue;
      const emitter = this.entityType(magnet).magnetEmitter;
      if (emitter === undefined) continue;
      const r2 = emitter.radiusPx * emitter.radiusPx;
      for (let j = 0; j < this.enemies.count; j++) {
        const target = this.enemies.items[j];
        if (j === i || target.hp <= 0) continue;
        this.pullToward(magnet.x, magnet.y, target, r2, emitter.pullPerSec, dt);
      }
      for (let j = 0; j < this.motes.count; j++) {
        this.pullToward(magnet.x, magnet.y, this.motes.items[j], r2, emitter.pullPerSec, dt);
      }
    }
  }

  private pullToward(x: number, y: number, target: { x: number; y: number }, maxD2: number, speed: number, dt: number): void {
    const dx = x - target.x;
    const dy = y - target.y;
    const d2 = dx * dx + dy * dy;
    if (d2 <= 0.001 || d2 > maxD2) return;
    const d = Math.sqrt(d2);
    const step = Math.min(d, speed * dt) / d;
    target.x += dx * step;
    target.y += dy * step;
  }

  /** Spawn a small particle burst (ability cast / detonation feedback). */
  fxBurst(x: number, y: number, color: number, count: number): void {
    spawnDeathBurst(this.particles, x, y, color, this.rng, count);
  }

  /** Expanding shockwave ring (ability cast blast). */
  fxRing(x: number, y: number, maxRadius: number, color: number, life = 0.4): void {
    spawnRing(this.rings, x, y, maxRadius, color, life);
  }

  /** Dense radial particle nova from a point. */
  fxNova(x: number, y: number, color: number, count: number, minSpeed: number, maxSpeed: number): void {
    spawnNova(this.particles, x, y, color, this.rng, count, minSpeed, maxSpeed);
  }

  /** Particles rushing inward toward a point (implosion). */
  fxImplosion(x: number, y: number, color: number, count: number, radius: number): void {
    spawnImplosion(this.particles, x, y, color, this.rng, count, radius);
  }

  /** Set (or clear, with []) the temporary boulder ring used by the hidden Maindan ability. */
  setTrapBoulders(list: Boulder[]): void {
    this.tempBoulders = list;
    this.tempBoulderHash = list.length > 0 ? buildBoulderHash(list, HASH_CELL) : null;
  }

  /** Build a player-readable temporary wall set around a source. This is deliberately
   * public for focused deterministic tests; production callers are level schedule and
   * creature data only. */
  triggerBloomPulse(count: number, sourceX = this.player.x, sourceY = this.player.y): void {
    const tuning = this.levelDefinition.bloomVines;
    if (tuning === undefined || count <= 0) return;
    for (let k = 0; k < count && this.vineWalls.length < tuning.maxActiveWalls; k++) {
      let placed = false;
      for (let attempt = 0; attempt < 12 && !placed; attempt++) {
        const angle = this.rng.range(0, Math.PI * 2);
        const distance = this.rng.range(tuning.minDistancePx, tuning.maxDistancePx);
        const x = clamp(sourceX + Math.cos(angle) * distance, tuning.wallRadius, ARENA.width - tuning.wallRadius);
        const y = clamp(sourceY + Math.sin(angle) * distance, tuning.wallRadius, ARENA.height - tuning.wallRadius);
        const dx = x - this.player.x;
        const dy = y - this.player.y;
        const safeRadius = tuning.wallRadius + this.player.radius + 36;
        if (dx * dx + dy * dy < safeRadius * safeRadius) continue;
        let overlaps = false;
        for (let i = 0; i < this.vineWalls.length; i++) {
          const existing = this.vineWalls[i];
          if (Math.hypot(existing.x - x, existing.y - y) < existing.radius + tuning.wallRadius + 14) {
            overlaps = true;
            break;
          }
        }
        if (overlaps) continue;
        this.vineWalls.push({ x, y, radius: tuning.wallRadius, telegraphSec: tuning.telegraphSec, lifeSec: tuning.durationSec });
        placed = true;
      }
    }
  }

  private updateVineWalls(dt: number): void {
    this.activeVineWalls.length = 0;
    for (let i = 0; i < this.vineWalls.length; i++) {
      const wall = this.vineWalls[i];
      if (wall.telegraphSec > 0) wall.telegraphSec -= dt;
      else wall.lifeSec -= dt;
      if (wall.lifeSec <= 0) {
        this.vineWalls.splice(i--, 1);
        continue;
      }
      if (wall.telegraphSec <= 0) this.activeVineWalls.push(wall);
    }
    this.vineHash.clear();
    for (let i = 0; i < this.activeVineWalls.length; i++) {
      const wall = this.activeVineWalls[i];
      this.vineHash.insert(i, wall.x, wall.y);
    }
  }

  /** Schedule or enemy-owned rails. A lane is rejected when it would pass too close to
   * the player, so the 800 ms warning remains an actual choice rather than a trap. */
  triggerGravityRails(count: number, sourceX = this.player.x, sourceY = this.player.y): void {
    const tuning = this.levelDefinition.gravityRails;
    if (tuning === undefined || count <= 0) return;
    for (let k = 0; k < count && this.gravityRails.length < tuning.maxActiveRails; k++) {
      for (let attempt = 0; attempt < 16; attempt++) {
        const angle = this.rng.range(0, Math.PI * 2);
        const distance = this.rng.range(tuning.minDistancePx, tuning.maxDistancePx);
        const x = clamp(sourceX + Math.cos(angle) * distance, tuning.lengthPx * 0.5, ARENA.width - tuning.lengthPx * 0.5);
        const y = clamp(sourceY + Math.sin(angle) * distance, tuning.lengthPx * 0.5, ARENA.height - tuning.lengthPx * 0.5);
        const direction = this.rng.range(0, Math.PI * 2);
        const rail: GravityRail = {
          x, y, dx: Math.cos(direction), dy: Math.sin(direction), length: tuning.lengthPx, width: tuning.widthPx,
          telegraphSec: tuning.telegraphSec, lifeSec: tuning.durationSec,
        };
        if (this.isInRail(this.player.x, this.player.y, this.player.radius + 30, rail)) continue;
        let overlaps = false;
        for (let i = 0; i < this.gravityRails.length; i++) {
          const other = this.gravityRails[i];
          if (Math.hypot(other.x - x, other.y - y) < 86) {
            overlaps = true;
            break;
          }
        }
        if (!overlaps) {
          this.gravityRails.push(rail);
          break;
        }
      }
    }
  }

  private rerouteGravityRails(targetX: number, targetY: number): void {
    for (let i = 0; i < this.gravityRails.length; i++) {
      const rail = this.gravityRails[i];
      if (rail.telegraphSec > 0) continue;
      const dx = targetX - rail.x;
      const dy = targetY - rail.y;
      const d = Math.hypot(dx, dy) || 1;
      rail.dx = dx / d;
      rail.dy = dy / d;
    }
  }

  private updateGravityRails(dt: number): void {
    for (let i = 0; i < this.gravityRails.length; i++) {
      const rail = this.gravityRails[i];
      if (rail.telegraphSec > 0) rail.telegraphSec -= dt;
      else rail.lifeSec -= dt;
      if (rail.lifeSec <= 0) this.gravityRails.splice(i--, 1);
    }
  }

  private applyGravityToPosition(target: { x: number; y: number }, radius: number, dt: number): boolean {
    for (let i = 0; i < this.gravityRails.length; i++) {
      const rail = this.gravityRails[i];
      if (rail.telegraphSec > 0 || !this.isInRail(target.x, target.y, radius, rail)) continue;
      const force =
        (this.levelDefinition.gravityRails?.forcePerSec ?? 0) *
        this.difficulty.hazardForceMul;
      target.x += rail.dx * force * dt;
      target.y += rail.dy * force * dt;
      return true;
    }
    return false;
  }

  private isInRail(x: number, y: number, radius: number, rail: GravityRail): boolean {
    const rx = x - rail.x;
    const ry = y - rail.y;
    const along = rx * rail.dx + ry * rail.dy;
    const across = -rx * rail.dy + ry * rail.dx;
    return Math.abs(along) <= rail.length * 0.5 + radius && Math.abs(across) <= rail.width * 0.5 + radius;
  }

  private spawnCatalystPool(x: number, y: number): void {
    const maxPools = 3;
    if (this.catalystPools.length >= maxPools) this.catalystPools.shift();
    this.catalystPools.push({ x, y, radius: 54, lifeSec: 5.5 });
  }

  private updateCatalystPools(dt: number): void {
    for (let i = 0; i < this.catalystPools.length; i++) {
      const pool = this.catalystPools[i];
      pool.lifeSec -= dt;
      if (pool.lifeSec <= 0) this.catalystPools.splice(i--, 1);
    }
  }

  private catalystDamageMultiplier(x: number, y: number): number {
    for (let i = 0; i < this.catalystPools.length; i++) {
      const pool = this.catalystPools[i];
      const dx = x - pool.x;
      const dy = y - pool.y;
      if (dx * dx + dy * dy <= pool.radius * pool.radius) return 1.35;
    }
    return 1;
  }

  private enemyType(key: string) {
    const type = this.levelDefinition.enemyTypes[key];
    if (type === undefined) throw new Error(`Level ${this.levelDefinition.id} references unknown enemy type: ${key}`);
    return type;
  }

  private entityType(enemy: Enemy) {
    const type = this.levelDefinition.enemyTypes[enemy.typeKey] ?? this.levelDefinition.bossTypes[enemy.typeKey];
    if (type === undefined) throw new Error(`Level ${this.levelDefinition.id} has an entity with unknown type: ${enemy.typeKey}`);
    return type;
  }

  private addXp(value: number): void {
    this.xp += value * this.difficulty.xpMul;
    while (this.xp >= this.xpNeeded) {
      this.xp -= this.xpNeeded;
      this.level++;
      this.pendingLevelUps++;
      this.xpNeeded = xpToNext(this.level);
      this.emitHaptic('levelUp');
      // Announce a freshly-unlocked ability.
      for (let i = 0; i < this.abilityDefs.length; i++) {
        if (this.abilityDefs[i].unlockLevel === this.level) {
          this.bannerText = `${this.abilityDefs[i].name.toUpperCase()} UNLOCKED · ${this.abilityDefs[i].label}`;
          this.bannerSec = BANNER_SEC;
        }
      }
    }
  }

  /** Final run score (plan §3.9). */
  score(): number {
    return computeScore({
      kills: this.kills,
      damageDealt: this.hitCtx.damageDealt,
      seconds: this.elapsedSec,
      bossKills: this.bossKills,
      level: this.level,
      victory: this.victory,
    });
  }

  /** Roll the choices for one level-up (deterministic via the run seed). */
  rollDraft(): DraftChoice[] {
    return rollDraft(this.rng, this.weapons, this.passiveLevels, this.allowedWeaponKeys, this.allowedPassiveKeys);
  }

  /** Apply one chosen upgrade and consume one pending level-up. */
  applyChoice(choice: DraftChoice): void {
    switch (choice.kind) {
      case 'reaction': // fuse into a new weapon
      case 'weapon-new':
      case 'weapon-up':
        this.weapons.add(choice.key); // add() levels up if already owned
        break;
      case 'passive-new':
      case 'passive-up':
        this.passiveLevels.set(choice.key, choice.level);
        this.recomputeMods();
        break;
      case 'heal':
        this.player.hp = Math.min(this.player.maxHp, this.player.hp + 30);
        break;
    }
    if (this.pendingLevelUps > 0) this.pendingLevelUps--;
    this.emitHaptic('selection');
  }

  private recomputeMods(): void {
    this.mods = computeModifiers(this.passiveLevels);
    this.hitCtx.mods = this.mods;
    const newMax = PLAYER.maxHp + this.mods.maxHpAdd;
    if (newMax > this.player.maxHp) this.player.hp += newMax - this.player.maxHp; // heal the gain
    this.player.maxHp = newMax;
  }
}
